import React, { useEffect, useState } from "react";
import { Stack } from "@mui/material";
import AuthInput from "../../components/Input/AuthInput";
import AuthButton from "../../components/Button/AuthButton";
import VerticalGap from "../../components/Gap/VerticalGap";
import LabelButton from "../../components/Button/LabelButton";
import useAppNavigation from "../../hooks/useAppNavigation";
import { PageActions, UserUpdatePasswordTy } from "../../config/types";

import { AuthApi } from "../../services/auth";
import { useLocation } from "react-router-dom";
import { validatePassword } from "../../utils/validators";
import useQueryParam from "../../hooks/useQueryParam";

import { useDmsForm } from "../../hooks/useDmsFrom";
import { usePageModels } from "../../hooks/usePageActions";
import ActionModal from "../../components/Modals/ActionModal";
import { DamsAppAsset } from "../../assets/registry";
import { useAppDispatch } from "../../store/hooks";
import { toggleNewUser } from "../../store/reducers/auth/account";
import { useKeyDownOnPress } from "../../hooks/useKeyDownOnPress";
import { alertDialogStore } from "../../store/dialogs";

interface SetPasswordProps {
  pageProps: any;
  isReset: boolean;
}

const INITAIL_VALUE: UserUpdatePasswordTy = {
  password: "",
  email: "",
  confirmPassword: "",
};

const MISMATCH_HELPER_TEXT = "password should be the same";
const SetPassword: React.FC<SetPasswordProps> = ({ pageProps, isReset }) => {
  const appNavigation = useAppNavigation();
  const goLoginPage=()=>appNavigation({path:"/auth", isSibling:true})

  const {
    form,
    updateForm,
    updateInputField,
    fieldHasError,
    setSubmitAttempt,
    isFormValid,
    getFormValues,
  } = useDmsForm<UserUpdatePasswordTy>(INITAIL_VALUE);

  const { email } = useLocation().state || {};
  const token = useQueryParam("token");

  const [pageAction, updatePageAction] = useState<PageActions>();
  const { models, openModel } = usePageModels();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (email) {  
      updateForm((prev) => ({
        ...prev,
        email: { isValid: true, value: email },
      }));
    }
    else if(token){
      const verifyRequest=async()=>{
        const response=await AuthApi.verifyPasswordResetRequest(token);
        if(response.success){
          updateForm((prev) => ({
            ...prev,
            email: { isValid: true, value: response.data.email },
          }));
        }else{
                    alertDialogStore.open({
                      title: "Request Verification",
                      subtitle: response.message,
                      onLeftBtnPress: () => appNavigation({path:"/auth"}),
                      onRightBtnPress: () =>appNavigation({path:"/auth"}),
                      leftBtnTxt: "Canel",
                      rightBtnTxt:"Okay",
                      isSuccess: false,
                      isFullScreen: true,
                    });
        }
      }
      verifyRequest();
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [email, token]);

  const [isProcessing, setProcess] = useState<boolean>(false);

  const handleSubmit = async () => {
    setSubmitAttempt(true);
    const isSame = form.password.value === form.confirmPassword?.value;
    const testPassword = validatePassword(form.password.value as string);
    const isValid = isFormValid() && testPassword.isValid;

    updateForm((prev) => ({
      ...prev,
      confirmPassword: {
        isValid: isSame,
        value: form?.confirmPassword?.value ?? "",
        errorMessage: isSame ? "" : MISMATCH_HELPER_TEXT,
      },
    }));
  
    if (isValid && isSame) {
      setProcess(true);
      const formData = getFormValues();
      let request;
      if (isReset) {

        request = AuthApi.passwordReset({
          password: formData.password,
          email: formData.email,
          token: token ?? "",
        });
        updatePageAction((p) => ({
          ...p,
          type: "edit",
          title: "Password reset",
        }));
      } else {
        request = AuthApi.activateUserAccount({
          password: formData.password,
          email,
        });
        updatePageAction((p) => ({
          ...p,
          type: "add",
          title: "Create Password",
        }));
      }
      const response = await request;
      updatePageAction((p) => ({
        ...p,
        success: response.success,
        message: response.message,
      }));
      if (pageAction?.type === "add" && response.success) {
        dispatch(
          toggleNewUser({
            is_active: response.data.is_active,
            is_verified: response.data.is_verified,
          })
        );
      }
      openModel("alert");
      setProcess(false);
    }
  };
  useKeyDownOnPress(handleSubmit);
  return (
    <Stack height={250}>
      <AuthInput
        value={form.password.value}
        error={fieldHasError("password")}
        errorMessage={form.password.errorMessage}
        type="password"
        label={"Password"}
        placeholder="Enter your password"
        onChange={updateInputField("password", "password")}
      />
      <VerticalGap height={20} />

      <AuthInput
          type="password"
          label={"Confirm Password"}
          placeholder="Re-enter your password"
        errorMessage={form.confirmPassword?.errorMessage}
        value={form.confirmPassword?.value}
        error={fieldHasError("confirmPassword")}
        onChange={updateInputField("confirmPassword")}
      />

      <VerticalGap height={20} />

      <Stack direction={"row"} gap={3} height={40}>
        <AuthButton
          title={pageProps?.BtnText}
          size={pageProps?.BtnType}
          showIcon={pageProps?.BtnIcon}
          onPress={handleSubmit}
          loading={isProcessing}
        />
        {isReset && (
          <LabelButton
            onPress={goLoginPage}
            label={pageProps.infoLabelIconText}
            direction={pageProps.labelDirection}
          />
        )}
      </Stack>
      <ActionModal
        show={models.showAlertModel}
        title={pageAction?.title ?? ""}
        leftIcon={
          pageAction?.success === false ? DamsAppAsset.icons.warning : undefined
        }
        subtitle={pageAction?.message ?? ""}
        onLeftBtnPress={goLoginPage}
        leftBtnTxt={"close"}
        onRightBtnPress={goLoginPage}
        rightBtnTxt={pageAction?.success ? "Login" : "Continue"}
        onClosePress={goLoginPage}
        isFullScreen
      />
    </Stack>
  );
};

export default SetPassword;
